import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "catalyst-ui-kit/dialog";
import { Button } from "catalyst-ui-kit/button";
import ConfirmButton from "./ConfirmButton";
// Polaris component, reused as-is inside the catalyst dialog (it is a plain
// form control with no popover/portal of its own, so it renders fine here).
import FileCategorySelect from "components/file-category-select/file-category-select";
import { setFileParent } from "services/upload-file.service";

interface MakeMainDocumentModalProps {
  companyId: string;
  fileId: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Detaches a document from its parent (parentId: null), turning it back into
 * a main document. The backend requires at least one category once the file
 * stops inheriting the parent's, so the category picker is required here.
 */
export default function MakeMainDocumentModal({
  companyId,
  fileId,
  open,
  onClose,
  onDone,
}: MakeMainDocumentModalProps) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCategories([]);
      setErrorMessage(null);
    }
  }, [open, fileId]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!companyId || !fileId || !categories.length || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await setFileParent(companyId, fileId, { parentId: null, categories });
      setIsSubmitting(false);
      onDone();
      onClose();
    } catch (e: any) {
      setIsSubmitting(false);
      setErrorMessage(e?.response?.data?.message || "error");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="sm">
      <DialogTitle>{t("make_main_document")}</DialogTitle>
      <DialogBody>
        <FileCategorySelect
          value={categories}
          onChange={setCategories}
          isRequired
          subject="document"
        />
        {errorMessage && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">
            {t(errorMessage)}
          </p>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain className="min-h-11" onClick={handleClose} disabled={isSubmitting}>
          {t("cancel")}
        </Button>
        <ConfirmButton
          onClick={handleConfirm}
          disabled={!categories.length || isSubmitting}
        >
          {t("make_main_document_confirm")}
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
}
